import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, module = null, role = null) => {
    const { data } = await api.post("/auth/login", { email, password, module });
    setUser(data);
    return data;
  };

  const register = async (name, email, password, module = null, role = null) => {
    const { data } = await api.post("/auth/register", { name, email, password, module, role });
    // New API returns { status: "pending", message, user } — do NOT set user.
    if (data && data.status === "pending") {
      return { pending: true, message: data.message, user: data.user };
    }
    // Backwards-compat: if server ever returns a user directly, set it.
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Non-fatal: session cookie may already be cleared. Log for visibility.
      console.warn("Logout request failed (continuing anyway):", e?.message || e);
    }
    setUser(false);
  };

  const setUserFromCallback = (u) => setUser(u);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, setUserFromCallback, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
